import React from 'react';
import styled from 'styled-components';
import { Link } from "react-router-dom";
import { makeStyles } from "@mui/styles";
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import useProjectStore from "../../../store-zustand/project";
import { AccountCircleOutlined, BeenhereOutlined, DataUsage, ExploreOutlined, LayersOutlined, WidgetsOutlined, SupportAgentRounded, InfoOutline } from '@mui/icons-material';

const useStyles = makeStyles((theme) => ({
  tooltip: {
    color: "white",
    fontSize: "12px !important",
    backgroundColor: "#5A5A5A",
    borderRadius: "3 !important",
    fontFamily: 'Roboto',
    padding: '8px 10px'
  }
}));

const Wrapper = styled.nav`
  z-index: 1;
  display: flex;
  background-color: #232323 !important;

  a, a:hover, a:focus, a:active {
    color: inherit;
    text-decoration: none;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.laptopMin}) {
      &.hide-mobile {
        display: none;
      }
    }
  }
  

  @media (min-width: ${({ theme }) => theme.breakpoints.desktopMin}) {
    position: relative;
    flex-direction: column;
    justify-content: space-between;
    flex: 0 0 5rem;
    height: 100vh;
    padding: 1.5rem 0 3rem;
    background: ${({ theme }) => theme.backgroundColor.sidebar};
  }

  button {
    @media (min-width: ${({ theme }) => theme.breakpoints.desktopMin}) {
      width: 100%;
      padding: 1.25rem .5rem;
      svg {
        fill: ${({ theme }) => theme.textColor.default};
      }
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.laptopMin}) {
      padding: 1.1rem 1.6rem;
      &.hide-mobile {
        display: none;
      }
    }
  }
`;

const ButtonIcon = styled(IconButton)`
  line-height: ${({ theme }) => theme.font.lineHeight.small};
  transition: all .35s;
  color: ${({ theme }) => theme.textColor.secondary} !important;
  background-color: transparent !important;
  min-height: 49px;
  
  svg {
    pointer-events: none;
    font-size: ${({ theme }) => theme.font.size.icon};
    fill: currentColor !important;
    transition: all .35s;
  }

  &:focus, &:hover, &.active {
    color: ${({ theme }) => theme.textColor.accent} !important;
  }

  &:hover {
    background-color: rgba(255, 255, 255, 0.05) !important;
  }
`;

const SidebarNav = ({ activeClass, handleSidebarNavClick }) => {
  const classes = useStyles();
  const CustomTooltip = (props) => {
    return <Tooltip classes={classes} {...props} />;
  }
  const { project_id } = useProjectStore();

  return (
    <Wrapper>
      <Link to={`/profile`}>
        <CustomTooltip title="Profile" aria-label="profile" className={classes.tooltip} placement="right">
          <ButtonIcon
            className={activeClass === 'profile' ? 'active' : null}
            onClick={() => handleSidebarNavClick('profile')}
            aria-label="Opens Profile"
          >
            <AccountCircleOutlined />
          </ButtonIcon>
        </CustomTooltip>
      </Link>
      <div>
        <Link to={`/search`} data-tour="step-1">
          <CustomTooltip title="Search" aria-label="search" className={classes.tooltip} placement="right">
            <ButtonIcon
              className={activeClass === 'search' ? 'active' : null}
              onClick={() => handleSidebarNavClick('search')}
              aria-label="Opens Map Search">
              <ExploreOutlined />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
        <Link to={project_id ? `/projects/${project_id}` : `/projects`}>
          <CustomTooltip title="Projects" aria-label="projects" className={classes.tooltip} placement="right">
            <ButtonIcon
              className={activeClass === 'projects' ? 'active hide-mobile' : 'hide-mobile'}
              onClick={() => handleSidebarNavClick('projects')}
              aria-label="Opens All Projects">
              <BeenhereOutlined />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
        <Link to={`/layers`}>
          <CustomTooltip title="Layers" aria-label="layers" className={classes.tooltip} placement="right">
            <ButtonIcon
              className={activeClass === 'layers' ? 'active' : null}
              onClick={() => handleSidebarNavClick('layers')}
              aria-label="Opens Layers">
              <LayersOutlined />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
        <Link to={`/apps`}>
          <CustomTooltip title="Apps" aria-label="apps" className={classes.tooltip} placement="right">
            <ButtonIcon
              className={activeClass === 'applibrary' ? 'active' : null}
              onClick={() => handleSidebarNavClick('applibrary')}
              aria-label="Opens App Library">
              <WidgetsOutlined />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
        <Link to={`/dashboard`}>
          <CustomTooltip title="Dashboard" aria-label="dashboard" className={classes.tooltip} placement="right">
            <ButtonIcon
              className={activeClass === 'dashboard' ? 'active' : null}
              onClick={() => handleSidebarNavClick('dashboard')}
              aria-label="Opens Dashboard">
              <DataUsage />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
        <Link to={`/`}>
          <CustomTooltip title="Help Tour" aria-label="info" className={classes.tooltip} placement="right">
            <ButtonIcon className={activeClass === 'info' ? 'active' : null} onClick={() => handleSidebarNavClick('info')} aria-label="Starts the Guided Tour">
              <InfoOutline />
            </ButtonIcon>
          </CustomTooltip>
        </Link>
      </div>
      <CustomTooltip title="Support" aria-label="support" className={classes.tooltip} placement="top">
        <ButtonIcon component="a" href="https://www.fastmap.ai/company/contact" target="_blank"
          rel="noopener noreferrer"
          className={activeClass === 'help' ? 'active hide-mobile' : 'hide-mobile'}
          aria-label="Opens Support Page">
          <SupportAgentRounded />
        </ButtonIcon>
      </CustomTooltip>
    </Wrapper>
  )
}

export default SidebarNav;